import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {Loaded} from 'Core/models/Async';
import {
  isLoaded,
  SalaryDeductionOccupationClassInfo,
  SalaryDeductionOccupationClassModel,
  UnassignedSalaryDeductionOccupationClassModel,
} from '../SalaryDeductionOccupationClassModel';

const riderCodes: RI.OrdinaryRiderCode[] = [
  {
    code: 'IWLADB',
    displayName: '',
    units: [],
  },
  {
    code: 'IWLCI',
    displayName: '',
    units: [50, 100],
  },
  {
    code: 'IWLCT',
    displayName: '',
    units: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  },
  {
    code: 'IWLLT',
    displayName: '',
    units: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  },
  {
    code: 'IWLST',
    displayName: '',
    units: [],
  },
  {
    code: 'IWLWP',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
  },
  {
    code: 'IWLTI',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
  },
];

type IWLProps = {
  plan: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  premiumType: StringUnion;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  autoIssueEligibility: boolean;
  replacement: boolean;
  riderInformation: RI.default;
  occClass: SalaryDeductionOccupationClassModel;
};

export class InvalidOrdinaryIwl {
  public readonly key = 'InvalidOrdinaryIwl';

  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly occClass: SalaryDeductionOccupationClassModel
  ) {}
}

export class ValidOrdinaryIwl {
  public static createApp({
    plan,
    premium,
    calculatedPremium,
    cwa,
    situsState,
    type,
    faceAmt,
    policyNumber,
    premiumType,
    priInsured,
    spouseInsured,
    autoIssueEligibility,
    replacement,
    riderInformation,
    occClass,
  }: IWLProps) {
    return this.create(
      plan,
      premium,
      calculatedPremium,
      cwa,
      situsState,
      type,
      faceAmt,
      policyNumber,
      premiumType,
      priInsured,
      spouseInsured,
      autoIssueEligibility,
      replacement,
      riderInformation,
      occClass
    );
  }

  public static create(
    plan: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    premiumType: StringUnion,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    autoIssueEligibility: boolean,
    replacement: boolean,
    riderInformation: RI.default,
    occClass: SalaryDeductionOccupationClassModel
  ) {
    return isNonEmptyString(plan) && isLoaded(occClass)
      ? new ValidOrdinaryIwl(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderInformation,
          occClass
        )
      : new InvalidOrdinaryIwl(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderInformation,
          occClass
        );
  }

  public readonly key = 'ValidOrdinaryIwl';
  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly occClass: Loaded<SalaryDeductionOccupationClassInfo>
  ) {}
}

export type OrdinaryIwl = InvalidOrdinaryIwl | ValidOrdinaryIwl;

export type OrdinaryIwlOptions<TReturn> = ToPartialMatchOptions<OrdinaryIwl, 'key', TReturn>;

export const matchOrdinaryIwl = <TReturn>(options: OrdinaryIwlOptions<TReturn>) => (app: OrdinaryIwl): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryIwl':
      return options.InvalidOrdinaryIwl ? options.InvalidOrdinaryIwl(app) : options.default(app);
    case 'ValidOrdinaryIwl':
      return options.ValidOrdinaryIwl ? options.ValidOrdinaryIwl(app) : options.default(app);
  }
};

export const EmptyOrdinaryIwl = new InvalidOrdinaryIwl(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new UnassignedSalaryDeductionOccupationClassModel()
);

export const changePlan = (plan: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    faceAmt: new EmptyString(),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
  });
export const changePremium = (premium: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSitusState = (situsState: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({
    ...app,
    type: NonEmptyString.create(type),
    faceAmt: new EmptyString(),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
  });
export const changeFaceAmt = (faceAmt: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePremiumType = (preType: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changePriInsured = (priInsured: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, autoIssueEligibility});
export const changeReplacement = (replacement: boolean) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, replacement});
export const changeOccClass = (occClass: SalaryDeductionOccupationClassModel) => (app: OrdinaryIwl) =>
  ValidOrdinaryIwl.createApp({...app, occClass});
export const removeRider = (index: number) => (app: OrdinaryIwl) => {
  const existingListAmounts: I.List<number> = app.riderInformation.amounts;
  const newListAmounts = existingListAmounts.delete(index);
  const existingListCodes: I.List<string> = app.riderInformation.codes;
  const newListCodes = existingListCodes.delete(index);
  const riderCodes: RI.RiderCode[] = app.riderInformation.riderData;

  return ValidOrdinaryIwl.createApp({
    ...app,
    riderInformation: new RI.default(newListCodes, newListAmounts, riderCodes),
  });
};
export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: OrdinaryIwl) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidOrdinaryIwl.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const planList = ['IWL'];
